import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { WIZARD_CONFIG } from '../../config/wizard.config';
import { CachedToken, TokenResponse, UploadTokenConfig } from '../models';

/**
 * Service for managing file upload tokens.
 * Fetches tokens from the API and provides configuration for ngx-media-upload.
 */
@Injectable()
export class FileTokenService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(WIZARD_CONFIG);

  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private cachedToken: CachedToken | null = null;

  /**
   * The fetch currently in progress, if any. Concurrent callers (e.g. several
   * gallery-upload fields mounting in the same change-detection pass) share this
   * single promise instead of each firing their own `files/upload-token` request.
   */
  private inFlight: Promise<CachedToken> | null = null;

  /** Whether a token request is in progress */
  readonly loading = this._loading.asReadonly();

  /** Error message if token fetch failed */
  readonly error = this._error.asReadonly();

  /**
   * Gets the upload configuration with fresh or cached token.
   * Returns uploadUrl and headers for ngx-media-upload.
   */
  async getUploadConfig(): Promise<UploadTokenConfig> {
    const token = await this.ensureToken();

    return {
      uploadUrl: token.uploadUrl,
      headers: {
        'X-Upload-Token': token.token,
      },
    };
  }

  /**
   * Ensures a valid token is available.
   * Returns cached token if still valid, otherwise fetches a new one.
   */
  async ensureToken(): Promise<CachedToken> {
    if (this.cachedToken && !this.isTokenExpired(this.cachedToken)) {
      return this.cachedToken;
    }

    return this.fetchToken();
  }

  /**
   * Forces a new token to be fetched, ignoring cache.
   */
  async refreshToken(): Promise<CachedToken> {
    return this.fetchToken();
  }

  /**
   * Clears the cached token (e.g., on logout).
   */
  clearToken(): void {
    this.cachedToken = null;
    this._error.set(null);
  }

  private fetchToken(): Promise<CachedToken> {
    // Coalesce concurrent fetches: while one request is in flight, every other
    // caller awaits the same promise so we never issue duplicate token requests.
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.doFetchToken().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async doFetchToken(): Promise<CachedToken> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const tokenEndpoint = this.config.fileUpload?.tokenEndpoint ?? 'files/upload-token/test';
      const url = `${this.config.apiUrl}${tokenEndpoint}`;
      const headers: Record<string, string> = {};

      if (this.config.fileUpload?.testToken) {
        headers['X-Test-Token'] = this.config.fileUpload.testToken;
      }

      const response = await firstValueFrom(this.http.get<TokenResponse>(url, { headers }));

      const cachedToken: CachedToken = {
        token: response.token,
        uploadUrl: response.uploadUrl,
        expiresAt: Date.now() + response.expiresIn * 1000,
      };

      this.cachedToken = cachedToken;
      return cachedToken;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch upload token';
      this._error.set(message);
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  private isTokenExpired(token: CachedToken): boolean {
    const bufferMs = 60 * 1000;
    return Date.now() >= token.expiresAt - bufferMs;
  }
}
